'use client';

import axios from 'axios';

import { useEffect } from 'react';

import { Button } from '@/components/ui/button';

import { COPY } from '@/shared/constants/copies';
import { MIXPANEL_APP_FEATURES, mixpanelEvents } from '@/shared/constants/mixpanel-events';
import { emitMixpanelEvent } from '@/shared/utils/mixpanel-utils';
import { captureCustomSentryError } from '@/shared/utils/sentry-utils';

export type PricingErrorSource = 'pricing_page' | 'plans_page' | 'landing_pricing';

type Props = {
	/** Kept for caller API parity; the button uses window.location.reload(). */
	onRetry: () => void;
	source: PricingErrorSource;
	error?: unknown;
};

const extractErrorDetails = (error: unknown) => {
	if (axios.isAxiosError(error)) {
		return {
			statusCode: error.response?.status ?? null,
			message:
				(error.response?.data as { message?: string })?.message || error.message || 'Unknown',
			isNetworkError: !error.response,
		};
	}
	if (error instanceof Error) {
		return { statusCode: null, message: error.message, isNetworkError: false };
	}
	return { statusCode: null, message: 'Unknown error', isNetworkError: false };
};

export const PricingErrorState = ({ source, error }: Props) => {
	useEffect(() => {
		const { statusCode, message, isNetworkError } = extractErrorDetails(error);

		emitMixpanelEvent(mixpanelEvents.VIEWED_PRICING_ERROR_SHOWN, {
			feature: MIXPANEL_APP_FEATURES.PRICING,
			source,
			error_status: statusCode,
			error_message: message,
			is_network_error: isNetworkError,
		});

		// Reported with its own category and elevated severity — pricing-load failures
		// are user-visible and must stand out from ordinary API errors.
		captureCustomSentryError(error, `[Pricing] Error state shown (source: ${source})`, {
			severity: 'high',
			tags: {
				error_category: 'pricing_load_failed',
				pricing_source: source,
				http_status: statusCode ?? 'unknown',
				is_network_error: isNetworkError,
			},
			fingerprint: ['pricing-error-state', source],
		});
	}, [error, source]);

	return (
		<div className="flex h-full min-h-[520px] flex-col items-center justify-center">
			<h1 className="mb-2 text-xl font-semibold text-gray-900">Something went wrong</h1>
			<p className="text-center text-sm text-gray-600">
				{COPY.pricing.loadError}
			</p>
			<Button
				className="mt-4"
				onClick={() => window.location.reload()}
			>
				Try again
			</Button>
		</div>
	);
};
